/**
 * Safely normalizes any tags input (null, undefined, string, object, Set, number, array, etc.)
 * into a clean, valid string[].
 * Never throws an error or fails execution.
 * Logs a warning in development mode when malformed tag data is encountered.
 */

export function normalizeTags(input: any): string[] {
  if (input === null || input === undefined) {
    return [];
  }

  try {
    // 1. If it's already an array
    if (Array.isArray(input)) {
      const result: string[] = [];
      for (const item of input) {
        if (typeof item === "string") {
          const trimmed = item.trim();
          if (trimmed) result.push(trimmed);
        } else if (item !== null && item !== undefined) {
          if (typeof item === "object") {
            const val = item.name || item.tag || item.label || item.value || item.title || item.text;
            if (typeof val === "string" && val.trim()) {
              result.push(val.trim());
            } else if (typeof val === "number" || typeof val === "boolean") {
              result.push(String(val));
            } else {
              if (process.env.NODE_ENV !== "production") {
                console.warn("[normalizeTags] Non-primitive item inside tags array:", item);
              }
            }
          } else if (typeof item === "number" || typeof item === "boolean") {
            result.push(String(item));
          }
        }
      }
      return result;
    }

    // 2. If it's a string
    if (typeof input === "string") {
      const trimmed = input.trim();
      if (!trimmed) return [];
      // Try parsing JSON array string like '["tag1", "tag2"]'
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return normalizeTags(parsed);
          }
        } catch (_) {
          // ignore JSON parse error
        }
      }
      // Handle comma-separated strings
      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [trimmed];
    }

    // 3. If it's a Set
    if (input instanceof Set) {
      return normalizeTags(Array.from(input));
    }

    // 4. If it's an object
    if (typeof input === "object") {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[normalizeTags] Non-array object passed as tags:", input);
      }
      if (Array.isArray(input.tags)) {
        return normalizeTags(input.tags);
      }
      if (Array.isArray(input.opportunityTags)) {
        return normalizeTags(input.opportunityTags);
      }
      if (Array.isArray(input.opportunity_tags)) {
        return normalizeTags(input.opportunity_tags);
      }
      const values = Object.values(input);
      if (values.length > 0) {
        return normalizeTags(values);
      }
      return [];
    }

    // 5. If it's a number or boolean
    if (typeof input === "number" || typeof input === "boolean") {
      return [String(input)];
    }
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[normalizeTags] Exception during tag normalization:", err);
    }
    return [];
  }

  if (process.env.NODE_ENV !== "production") {
    console.warn("[normalizeTags] Could not normalize tags input:", input);
  }
  return [];
}
